import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ChevronRight, Copy, Lock, Trophy, X } from 'lucide-react'
import { clsx } from 'clsx'
import { ApiError } from '@/shared/api/client'
import { useUserLeagues } from '@/features/league/hooks/useUserLeagues'
import type { LeagueSummary } from '@/features/league/types'
import { useCopyPreviewMutation } from '../queries'
import type { CopyBlocker, CopyBlockerCode, CopyPayload, PreviewCopySquadResult, TransferPenaltyPreview } from '../types'
import styles from './CopySquadModal.module.css'

type CopySquadModalProps = {
  targetSquadId:        string
  currentLeagueId:      string
  currentCompetitionId: string
  onConfirm:            (payload: CopyPayload) => void
  onClose:              () => void
}

export function CopySquadModal({
  targetSquadId,
  currentLeagueId,
  currentCompetitionId,
  onConfirm,
  onClose,
}: CopySquadModalProps) {
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)
  const { data: allLeagues, isLoading: isLoadingLeagues } = useUserLeagues()
  const previewMutation = useCopyPreviewMutation()

  // ESC closes the modal at any step (unless preview is loading)
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !previewMutation.isPending) onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose, previewMutation.isPending])

  const peerLeagues = useMemo<LeagueSummary[]>(() => {
    if (!allLeagues) return []
    return allLeagues.filter(
      l => l.competitionId === currentCompetitionId && l.id !== currentLeagueId,
    )
  }, [allLeagues, currentLeagueId, currentCompetitionId])

  function handlePickLeague(leagueId: string) {
    setSelectedSourceId(leagueId)
    previewMutation.mutate({ targetSquadId, sourceLeagueId: leagueId })
  }

  function handleBack() {
    setSelectedSourceId(null)
    previewMutation.reset()
  }

  const selectedLeague = peerLeagues.find(l => l.id === selectedSourceId)

  return (
    <>
      <div className={styles.backdrop} onClick={previewMutation.isPending ? undefined : onClose} aria-hidden="true" />
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="copy-squad-title">
        <div className={styles.header}>
          {selectedSourceId && !previewMutation.isPending && (
            <button type="button" className={styles.backBtn} onClick={handleBack} aria-label="Voltar">
              <ArrowLeft size={16} />
            </button>
          )}
          <div className={styles.iconWrap} aria-hidden="true">
            <Copy size={16} strokeWidth={2.5} />
          </div>
          <h2 id="copy-squad-title" className={styles.title}>
            {selectedSourceId ? `Copiar de ${selectedLeague?.name ?? ''}` : 'Copiar de outra liga'}
          </h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Fechar" disabled={previewMutation.isPending}>
            <X size={16} />
          </button>
        </div>

        <div className={styles.body}>
          {!selectedSourceId ? (
            <SourceLeaguePicker
              isLoading={isLoadingLeagues}
              leagues={peerLeagues}
              onPick={handlePickLeague}
            />
          ) : (
            <PreviewStep
              isPending={previewMutation.isPending}
              error={previewMutation.error}
              data={previewMutation.data}
              sourceLeagueId={selectedSourceId}
              onConfirm={onConfirm}
              onCloseModal={onClose}
            />
          )}
        </div>
      </div>
    </>
  )
}

// ── Step 1 — source league picker ────────────────────────────────────────────

function SourceLeaguePicker({
  isLoading,
  leagues,
  onPick,
}: {
  isLoading: boolean
  leagues:   LeagueSummary[]
  onPick:    (leagueId: string) => void
}) {
  if (isLoading) {
    return <div className={styles.empty}>Carregando…</div>
  }
  if (leagues.length === 0) {
    return (
      <div className={styles.empty}>
        <Trophy size={28} strokeWidth={1.5} />
        <p>Você não tem outras ligas nesta competição.</p>
        <span className={styles.emptyHint}>
          Crie ou entre em outra liga da mesma competição para copiar uma escalação.
        </span>
      </div>
    )
  }

  return (
    <ul className={styles.leagueList}>
      {leagues.map(l => (
        <li key={l.id}>
          <button
            type="button"
            className={styles.leagueRow}
            onClick={() => onPick(l.id)}
          >
            <div className={styles.leagueRowMain}>
              <span className={styles.leagueName}>{l.name}</span>
              <span className={styles.leagueMeta}>
                {l.format === 'HEAD_TO_HEAD' ? 'Mata-mata' : 'Pontos corridos'} · {l.memberCount} membros
              </span>
            </div>
            <ChevronRight size={16} className={styles.chevron} />
          </button>
        </li>
      ))}
    </ul>
  )
}

// ── Step 2 — preview, blockers or payload + penalty ──────────────────────────

function PreviewStep({
  isPending,
  error,
  data,
  sourceLeagueId,
  onConfirm,
  onCloseModal,
}: {
  isPending:      boolean
  error:          unknown
  data:           PreviewCopySquadResult | undefined
  sourceLeagueId: string
  onConfirm:      (payload: CopyPayload) => void
  onCloseModal:   () => void
}) {
  if (isPending) {
    return (
      <div className={styles.loading}>
        <span className={styles.spinner} aria-hidden="true" />
        <span>Verificando elegibilidade…</span>
      </div>
    )
  }

  if (error) {
    const message =
      error instanceof ApiError
        ? error.detail
        : 'Falha ao carregar a pré-visualização. Tente novamente.'
    return <div className={styles.error}>{message}</div>
  }

  if (!data) return null

  if (!data.isEligible) {
    return (
      <BlockerList
        blockers={data.blockers}
        sourceLeagueId={sourceLeagueId}
        onCloseModal={onCloseModal}
      />
    )
  }

  return (
    <EligiblePreview
      payload={data.payload!}
      penaltyPreview={data.penaltyPreview}
      onConfirm={onConfirm}
    />
  )
}

// ── Blockers (§13.4) ─────────────────────────────────────────────────────────

const BLOCKER_TITLES: Record<CopyBlockerCode, string> = {
  copy_window_closed:              'Janela fechada nesta liga',
  copy_cross_competition:          'Liga de competição diferente',
  copy_same_league:                'Não é possível copiar da mesma liga',
  source_squad_eliminated_players: 'Jogadores eliminados na escalação de origem',
  source_squad_over_budget:        'Escalação de origem acima do orçamento desta liga',
  source_squad_incomplete:         'Escalação de origem incompleta',
  source_squad_not_found:          'Escalação de origem não encontrada',
  squad_not_found:                 'Escalação não encontrada',
  forbidden:                       'Sem permissão',
}

function BlockerList({
  blockers,
  sourceLeagueId,
  onCloseModal,
}: {
  blockers:       CopyBlocker[]
  sourceLeagueId: string
  onCloseModal:   () => void
}) {
  const navigate = useNavigate()

  function navigateToSource() {
    onCloseModal()
    void navigate({
      to:     '/league/$leagueId',
      params: { leagueId: sourceLeagueId },
      search: { tab: 'escalacoes', gw: undefined },
    })
  }

  return (
    <div className={styles.blockers}>
      <p className={styles.blockerIntro}>
        Resolva os problemas abaixo na liga de origem e abra esta janela novamente.
      </p>
      <ul className={styles.blockerList}>
        {blockers.map((b, i) => (
          <li key={i} className={styles.blockerItem}>
            <Lock size={13} strokeWidth={2.5} className={styles.blockerIcon} />
            <div className={styles.blockerBody}>
              <span className={styles.blockerTitle}>
                {BLOCKER_TITLES[b.errorCode as CopyBlockerCode] ?? b.errorCode}
              </span>
              {b.message && <span className={styles.blockerMessage}>{b.message}</span>}
              {b.playerIds && b.playerIds.length > 0 && (
                <span className={styles.blockerCount}>
                  {b.playerIds.length} jogador(es) afetado(s)
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
      {hasSourceFixableBlocker(blockers) && (
        <button type="button" className={styles.primaryBtn} onClick={navigateToSource}>
          Ir para a liga de origem
        </button>
      )}
    </div>
  )
}

function hasSourceFixableBlocker(blockers: CopyBlocker[]): boolean {
  return blockers.some(b =>
    b.errorCode === 'source_squad_eliminated_players' ||
    b.errorCode === 'source_squad_over_budget' ||
    b.errorCode === 'source_squad_incomplete',
  )
}

// ── Eligible preview ─────────────────────────────────────────────────────────

function EligiblePreview({
  payload,
  penaltyPreview,
  onConfirm,
}: {
  payload:        CopyPayload
  penaltyPreview: TransferPenaltyPreview | undefined
  onConfirm:      (payload: CopyPayload) => void
}) {
  const totalCost = useMemo(() => {
    const startersCost = payload.starters.reduce((s, p) => s + p.price, 0)
    const benchCost    = payload.benchOutfield.reduce((s, p) => s + p.price, 0)
    return startersCost + benchCost + payload.benchGk.price
  }, [payload])

  const hasPenalty = (penaltyPreview?.penaltyPoints ?? 0) < 0

  return (
    <div className={styles.preview}>
      <div className={styles.summaryRow}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Jogadores</span>
          <span className={styles.summaryValue}>15</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Custo total</span>
          <span className={styles.summaryValue}>${totalCost.toFixed(1)}</span>
        </div>
      </div>

      {penaltyPreview && (
        <PenaltyBreakdown preview={penaltyPreview} />
      )}

      {!penaltyPreview && (
        <p className={styles.freeNote}>
          Esta cópia é gratuita — você ainda não tem uma escalação salva nesta liga.
        </p>
      )}

      <button
        type="button"
        className={clsx(styles.primaryBtn, hasPenalty && styles.primaryBtnDanger)}
        onClick={() => onConfirm(payload)}
      >
        {hasPenalty
          ? `Usar esta escalação (${penaltyPreview!.penaltyPoints} pts)`
          : 'Usar esta escalação'}
      </button>
      <p className={styles.commitNote}>
        A escalação será carregada no editor. Você ainda precisa clicar em <strong>Salvar</strong> para confirmar.
      </p>
    </div>
  )
}

function PenaltyBreakdown({ preview }: { preview: TransferPenaltyPreview }) {
  const isOptionB = preview.transferOption === 'OPTION_B'
  const hasPenalty = preview.penaltyPoints < 0

  return (
    <div className={styles.breakdown}>
      <div className={styles.row}>
        <span className={styles.rowLabel}>Total de transferências</span>
        <span className={styles.rowValue}>{preview.outgoingTotal}</span>
      </div>
      {preview.eliminationCreditsUsed > 0 && (
        <div className={styles.row}>
          <span className={styles.rowLabel}>Créditos de eliminação usados</span>
          <span className={clsx(styles.rowValue, styles.credit)}>−{preview.eliminationCreditsUsed}</span>
        </div>
      )}
      {preview.byeCreditsUsed > 0 && (
        <div className={styles.row}>
          <span className={styles.rowLabel}>Créditos de folga usados</span>
          <span className={clsx(styles.rowValue, styles.credit)}>−{preview.byeCreditsUsed}</span>
        </div>
      )}
      {!isOptionB && (
        <div className={styles.row}>
          <span className={styles.rowLabel}>Transferências gratuitas usadas</span>
          <span className={clsx(styles.rowValue, styles.free)}>{preview.freeTransfersUsed} / 3</span>
        </div>
      )}
      {hasPenalty && (
        <>
          <hr className={styles.divider} />
          <div className={styles.row}>
            <span className={styles.rowLabel}>Transferências pagas</span>
            <span className={clsx(styles.rowValue, styles.paid)}>{preview.paidExtras} × −5 pts</span>
          </div>
          <div className={clsx(styles.row, styles.totalRow)}>
            <span className={styles.totalLabel}>Multa total</span>
            <span className={styles.totalValue}>{preview.penaltyPoints} pts</span>
          </div>
        </>
      )}
    </div>
  )
}
